// MeetScribe Dashboard Store Module
// Dashboard for viewing all meetings with quick stats and filters
import axiosConfig from '@/lib/axiosConfig';

const state = {
  // Quick Stats
  quickStats: {
    meetings: { needs_attention: 0, active: 0, completed: 0, processing: 0 },
    tasks: { overdue: 0, in_progress: 0, my_tasks: 0 },
    open_questions: 0
  },

  // Meetings list
  meetings: [],

  // Filters
  filters: {
    status: 'all',          // all | needs_attention | active | completed | processing
    period: 'last_30_days', // today | last_7_days | last_30_days | this_month | all_time
    search: '',
    sortBy: 'priority'      // priority | date_desc | date_asc | activity
  },

  // Pagination
  pagination: {
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  },

  // Loading states
  loading: false,
  statsLoading: false,
  error: null
};

const getters = {
  getQuickStats: (state) => state.quickStats,
  getMeetings: (state) => state.meetings,
  getFilters: (state) => state.filters,
  getPagination: (state) => state.pagination,
  isLoading: (state) => state.loading,
  isStatsLoading: (state) => state.statsLoading,
  getError: (state) => state.error,

  // Group meetings by work_status
  groupedMeetings: (state) => {
    const groups = {
      needs_attention: [],
      active: [],
      processing: [],
      completed: []
    };

    state.meetings.forEach(meeting => {
      const status = meeting.work_status || 'active';
      if (groups[status]) {
        groups[status].push(meeting);
      } else {
        groups.active.push(meeting);
      }
    });

    return groups;
  },

  // Apply local filters (search is applied locally for responsiveness)
  filteredMeetings: (state) => {
    let filtered = [...state.meetings];

    // Apply search filter locally
    if (state.filters.search && state.filters.search.trim()) {
      const searchLower = state.filters.search.toLowerCase().trim();
      filtered = filtered.filter(meeting => {
        const title = (meeting.title || '').toLowerCase();
        const description = (meeting.description || '').toLowerCase();
        const participants = (meeting.participants_preview || '').toLowerCase();
        return title.includes(searchLower) ||
               description.includes(searchLower) ||
               participants.includes(searchLower);
      });
    }

    // Apply local sorting
    switch (state.filters.sortBy) {
      case 'date_desc':
        filtered.sort((a, b) => new Date(b.meeting_date || b.created_at) - new Date(a.meeting_date || a.created_at));
        break;
      case 'date_asc':
        filtered.sort((a, b) => new Date(a.meeting_date || a.created_at) - new Date(b.meeting_date || b.created_at));
        break;
      case 'activity':
        filtered.sort((a, b) => new Date(b.last_activity || b.updated_at) - new Date(a.last_activity || a.updated_at));
        break;
      case 'priority':
      default:
        // Priority sorting: needs_attention first, then by date
        filtered.sort((a, b) => {
          const priorityOrder = { needs_attention: 0, active: 1, processing: 2, completed: 3 };
          const priorityA = priorityOrder[a.work_status] ?? 1;
          const priorityB = priorityOrder[b.work_status] ?? 1;
          if (priorityA !== priorityB) return priorityA - priorityB;
          return new Date(b.meeting_date || b.created_at) - new Date(a.meeting_date || a.created_at);
        });
        break;
    }

    return filtered;
  },

  // Check if there are overdue tasks
  hasOverdueTasks: (state) => {
    return state.quickStats.tasks.overdue > 0;
  },

  // Get total meetings count for current filter
  totalMeetingsCount: (state) => {
    if (state.filters.status === 'all') {
      return state.pagination.total;
    }
    return state.quickStats.meetings[state.filters.status] || 0;
  }
};

const mutations = {
  SET_QUICK_STATS(state, stats) {
    // Transform flat API response to nested structure expected by components
    // API returns: { needs_attention, active, processing, completed, overdue_tasks, in_progress_tasks, ... }
    // Components expect: { meetings: {...}, tasks: {...}, open_questions }
    if (stats.meetings && stats.tasks) {
      // Already in correct format
      state.quickStats = stats;
    } else {
      // Transform flat structure
      state.quickStats = {
        meetings: {
          needs_attention: stats.needs_attention || 0,
          active: stats.active || 0,
          completed: stats.completed || 0,
          processing: stats.processing || 0
        },
        tasks: {
          overdue: stats.overdue_tasks || 0,
          in_progress: stats.in_progress_tasks || 0,
          my_tasks: stats.my_tasks || 0
        },
        open_questions: stats.open_questions || 0
      };
    }
  },

  SET_MEETINGS(state, meetings) {
    state.meetings = meetings;
  },

  APPEND_MEETINGS(state, meetings) {
    state.meetings = [...state.meetings, ...meetings];
  },

  SET_FILTER(state, { filterName, value }) {
    state.filters[filterName] = value;
  },

  RESET_FILTERS(state) {
    state.filters = {
      status: 'all',
      period: 'last_30_days',
      search: '',
      sortBy: 'priority'
    };
  },

  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total || 0,
      limit: pagination.limit || 50,
      offset: pagination.offset || 0,
      hasMore: pagination.hasMore || false
    };
  },

  INCREMENT_OFFSET(state) {
    state.pagination.offset += state.pagination.limit;
  },

  RESET_PAGINATION(state) {
    state.pagination.offset = 0;
    state.pagination.hasMore = false;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_STATS_LOADING(state, loading) {
    state.statsLoading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  // Update single meeting in list (for real-time updates)
  UPDATE_MEETING(state, updatedMeeting) {
    const index = state.meetings.findIndex(m => m.id === updatedMeeting.id);
    if (index !== -1) {
      state.meetings.splice(index, 1, { ...state.meetings[index], ...updatedMeeting });
    }
  },

  // Remove meeting from list
  REMOVE_MEETING(state, meetingId) {
    state.meetings = state.meetings.filter(m => m.id !== meetingId);
  }
};

const actions = {
  // Fetch dashboard with meetings list and quick stats
  async fetchDashboard({ commit, state, rootState }, { resetPagination = true } = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    if (resetPagination) {
      commit('RESET_PAGINATION');
    }

    try {
      const params = {
        action: 'get_meetings_dashboard',
        status: state.filters.status,
        period: state.filters.period,
        sortBy: state.filters.sortBy,
        limit: state.pagination.limit,
        offset: resetPagination ? 0 : state.pagination.offset
      };

      // Добавляем chatbot_name из конфигурации для JWT авторизации
      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.chatbot_name = rootState.app.configData.chatbot_name;
      }

      // Don't send search to API - it's filtered locally for better UX
      // If you want server-side search, uncomment:
      // if (state.filters.search) {
      //   params.search = state.filters.search;
      // }

      const response = await axiosConfig.get('', { params });

      if (response.data) {
        // Set meetings
        if (response.data.meetings) {
          if (resetPagination) {
            commit('SET_MEETINGS', response.data.meetings);
          } else {
            commit('APPEND_MEETINGS', response.data.meetings);
          }
        }

        // Set quick stats
        if (response.data.quickStats) {
          commit('SET_QUICK_STATS', response.data.quickStats);
        }

        // Set pagination
        if (response.data.pagination) {
          commit('SET_PAGINATION', response.data.pagination);
        } else {
          // Calculate hasMore based on response
          const hasMore = response.data.meetings &&
                          response.data.meetings.length >= state.pagination.limit;
          commit('SET_PAGINATION', {
            total: response.data.total || state.meetings.length,
            limit: state.pagination.limit,
            offset: resetPagination ? 0 : state.pagination.offset,
            hasMore
          });
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching meetings dashboard:', error);
      commit('SET_ERROR', error.message || 'Failed to load dashboard');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Set filter and reload data
  async setFilter({ commit, dispatch }, { filterName, value }) {
    commit('SET_FILTER', { filterName, value });

    // Search is applied locally, no need to refetch
    if (filterName === 'search') {
      return;
    }

    // For other filters, refetch from server
    await dispatch('fetchDashboard', { resetPagination: true });
  },

  // Load more meetings (pagination)
  async loadMore({ commit, state, dispatch }) {
    if (!state.pagination.hasMore || state.loading) {
      return;
    }

    commit('INCREMENT_OFFSET');
    await dispatch('fetchDashboard', { resetPagination: false });
  },

  // Refresh only stats (lightweight update)
  async refreshStats({ commit, rootState }) {
    commit('SET_STATS_LOADING', true);

    try {
      const params = {
        action: 'get_meetings_dashboard',
        statsOnly: true
      };

      // Добавляем chatbot_name из конфигурации для JWT авторизации
      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.chatbot_name = rootState.app.configData.chatbot_name;
      }

      const response = await axiosConfig.get('', { params });

      if (response.data && response.data.quickStats) {
        commit('SET_QUICK_STATS', response.data.quickStats);
      }

      return response.data;
    } catch (error) {
      console.error('Error refreshing stats:', error);
      // Don't set error for stats refresh - it's not critical
    } finally {
      commit('SET_STATS_LOADING', false);
    }
  },

  // Reset all filters to defaults
  resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS');
    return dispatch('fetchDashboard', { resetPagination: true });
  },

  // Alias for resetFilters (used by Dashboard)
  clearAllFilters({ dispatch }) {
    return dispatch('resetFilters');
  },

  // Update meeting locally (for optimistic updates)
  updateMeetingLocally({ commit }, meeting) {
    commit('UPDATE_MEETING', meeting);
  },

  // Remove meeting locally
  removeMeetingLocally({ commit }, meetingId) {
    commit('REMOVE_MEETING', meetingId);
  },

  // Clear dashboard state (on logout or navigation away)
  clearDashboard({ commit }) {
    commit('SET_MEETINGS', []);
    commit('SET_QUICK_STATS', {
      meetings: { needs_attention: 0, active: 0, completed: 0, processing: 0 },
      tasks: { overdue: 0, in_progress: 0, my_tasks: 0 },
      open_questions: 0
    });
    commit('RESET_FILTERS');
    commit('RESET_PAGINATION');
    commit('SET_ERROR', null);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
